/**
 * The article (web.md §9): the only component that puts message HTML into the DOM — always through
 * `sanitizeArticle` (pinned DOMPurify), inserted as a DocumentFragment. Media the engine kept aside
 * (videos, audio) are listed as links; nothing is embedded (CSP frame-src 'none').
 */

import { useLayoutEffect, useRef } from 'react';
import type { Message } from '@digester/core';
import { Icon } from '../icons/Icon.js';
import { sanitizeArticle } from './sanitize.js';

export interface ArticleBodyProps {
  message: Message;
  showImages: boolean;
  tagNames: string[];
}

const MEDIA_LABEL: Record<string, string> = { video: 'Video', audio: 'Audio', image: 'Image', link: 'Link' };

function httpUrl(url: string): string | null {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

export function ArticleBody({ message, showImages, tagNames }: ArticleBodyProps) {
  const ref = useRef<HTMLDivElement>(null);
  const html = message.bodyHtml;
  const textFallback = html === null || html.trim() === '';

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (textFallback) {
      el.replaceChildren();
      return;
    }
    el.replaceChildren(sanitizeArticle(html, { showImages, url: message.url, leadImage: message.imageUrl, leadImageAlt: '' }));
  }, [html, textFallback, showImages, message.url, message.imageUrl]);

  const media = message.media.filter((m) => m.type !== 'image' && httpUrl(m.url) !== null);
  return (
    <div className="article-body">
      {textFallback ? (
        <p>{message.contentState === 'pending' ? 'The text of this message has not arrived yet.' : message.excerpt}</p>
      ) : null}
      <div className="article-html" ref={ref} />
      {media.length ? (
        <ul className="article-media">
          {media.map((m, i) => (
            <li key={`${m.url}-${i}`}>
              <a href={httpUrl(m.url) ?? undefined} target="_blank" rel="noopener noreferrer nofollow">
                <Icon name={m.type === 'video' ? 'circle-play' : m.type === 'audio' ? 'headphones' : 'external-link'} />
                {m.title ? m.title : MEDIA_LABEL[m.type] ?? 'Media'}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {tagNames.length ? (
        <div className="article-tags">
          {tagNames.map((t) => (
            <span key={t}>
              <Icon name="tag" />
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
